<template>
  <GobSection id="soluciones" tono="alterna">
    <div class="max-w-3xl">
      <h2 class="text-gob-5 leading-tight font-extrabold tracking-gob-tighter text-balance text-main">
        Cuatro soluciones que trabajan como una sola.
      </h2>
      <p class="mt-4 text-gob-2 leading-relaxed text-[#495057]">
        Cada una responde a un problema que su entidad ya reconoce. Y todas trabajan sobre la misma información.
      </p>
    </div>

    <!--
      Acordeón WAI-ARIA en tarjetas. Siempre hay una solución abierta: cerrar la abierta abre la
      siguiente (o la anterior, si es la última), así la persona puede cerrarla sin dejar la sección vacía.
    -->
    <ul class="mt-12 grid gap-4" role="list">
      <li
        v-for="(solucion, indice) in soluciones"
        :key="solucion.id"
        class="solucion rounded-gob-lg border bg-surface"
        :class="{ 'is-abierta': indice === activa }"
        :style="{ '--solucion-color': solucion.color }"
      >
        <h3>
          <button
            :id="`solucion-boton-${solucion.id}`"
            type="button"
            class="acordeon-boton flex w-full cursor-pointer items-center gap-4 rounded-gob-lg px-6 py-6 text-left"
            :aria-expanded="String(indice === activa)"
            :aria-controls="`solucion-panel-${solucion.id}`"
            @click="alternar(indice)"
          >
            <span class="solucion-punto size-3 shrink-0 rounded-full" aria-hidden="true"></span>
            <span class="grow text-gob-3 leading-tight font-extrabold tracking-gob-tight text-main">
              {{ solucion.nombre }}
            </span>
            <IconChevronDown class="acordeon-chevron size-6 shrink-0 text-[#495057]" aria-hidden="true" />
          </button>
        </h3>

        <div
          :id="`solucion-panel-${solucion.id}`"
          role="region"
          :aria-labelledby="`solucion-boton-${solucion.id}`"
          class="acordeon-panel"
          :class="{ 'is-abierto': indice === activa }"
          :inert="indice === activa ? null : ''"
        >
          <div class="min-h-0 overflow-hidden">
            <div class="grid gap-8 pr-6 pb-8 pl-13 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] lg:items-center lg:gap-14">
              <div>
                <p class="text-gob-2 leading-relaxed text-main">{{ solucion.linea }}</p>
                <ul class="mt-5 grid gap-2.5" role="list">
                  <li v-for="item in solucion.items" :key="item" class="flex items-start gap-2.5 text-[#495057]">
                    <IconCheck class="solucion-check mt-1 size-4 shrink-0" aria-hidden="true" />
                    <span>{{ item }}</span>
                  </li>
                </ul>
                <div class="mt-7">
                  <GobButton :to="solucion.to" con-flecha>{{ solucion.cta }}</GobButton>
                </div>
              </div>

              <!-- La captura se abre a pantalla completa, sin el recorte 16:9 de la miniatura. -->
              <GobImagenAmpliable
                v-if="solucion.imagen"
                class="w-full"
                :src="solucion.imagen"
                :alt="solucion.alt"
                :titulo="solucion.nombre"
                clase-imagen="rounded-gob-md border border-[#DEE2E6]"
              />
              <!-- Captura pendiente: el espacio reserva la proporción para no mover la página cuando llegue. -->
              <GobMedia
                v-else
                class="rounded-gob-md"
                :etiqueta="`Espacio reservado para una captura de ${solucion.nombre}`"
                :texto="`Captura de ${solucion.nombre}`"
                relacion="16 / 9"
              />
            </div>
          </div>
        </div>
      </li>
    </ul>
  </GobSection>
</template>

<script>
import { IconCheck, IconChevronDown } from '@tabler/icons-vue'
import GobButton from '@/components/GobButton.vue'
import GobImagenAmpliable from '@/components/GobImagenAmpliable.vue'
import GobMedia from '@/components/GobMedia.vue'
import GobSection from '@/components/GobSection.vue'
import imagenVision360 from '@/assets/home/solución/CaracterizadorCiudadanoVision360.webp'
import imagenPlanesAccion from '@/assets/home/solución/PlanesAcción.webp'
import imagenPlanesDesarrollo from '@/assets/home/solución/PlanesDesarrollo.webp'
import imagenGestionTareas from '@/assets/home/solución/GestiónTareasProyectosAccionesRutina.webp'
import { solucionPorId } from '@/data/soluciones'


const SOLUCIONES = [
  {
    id: 'vision-360',
    linea: 'Conozca a cada ciudadano y priorice a quien más lo necesita.',
    items: [
      'Expediente único digital con toda la historia del ciudadano',
      'Índice de vulnerabilidad calculado con 23 factores',
      'Mapas de calor de la población en el territorio'
    ],
    imagen: imagenVision360,
    alt: 'Mapa de calor de la distribución territorial de beneficiarios en Gob 24/7'
  },
  {
    id: 'planes-desarrollo',
    linea: 'Deje de reportar lo que ya pasó: gestione el cumplimiento de sus metas en tiempo real.',
    items: [
      'Líneas estratégicas, programas y metas en un solo lugar',
      'Presupuesto por meta frente al avance real',
      'Indicadores de producto y de resultado'
    ],
    imagen: imagenPlanesDesarrollo,
    alt: ''
  },
  {
    id: 'planes-accion',
    linea: 'Convierta cada meta del plan en el trabajo diario de sus dependencias.',
    items: [
      'Actividades de inversión y tareas por vigencia',
      'Responsables y evidencias revisadas en el mismo lugar',
      'Alertas cuando una tarea se vence'
    ],
    imagen: imagenPlanesAccion,
    alt: ''
  },
  {
    id: 'gestion-tareas-proyectos',
    linea: 'Delegue una tarea y deje que el sistema le haga el seguimiento por usted.',
    items: [
      'Tareas y subtareas con responsable y fecha',
      'Estado que se actualiza solo: pendiente, en curso, completada o vencida',
      'Evidencias aprobadas o devueltas para corregir'
    ],
    imagen: imagenGestionTareas,
    alt: ''
  }
]

/**
 * HomeSoluciones
 * Resume las cuatro soluciones en un acordeón de tarjetas: nombre siempre visible y, en el panel abierto,
 * la línea diferenciadora, tres capacidades, el CTA a su página y la captura.
 */
export default {
  name: 'HomeSoluciones',
  components: {
    GobButton,
    GobImagenAmpliable,
    GobMedia,
    GobSection,
    IconCheck,
    IconChevronDown
  },
  data() {
    return {
      // Nombre, ruta, color y CTA desde src/data/soluciones.js; aquí solo vive el copy propio del Inicio.
      soluciones: SOLUCIONES.map((solucion) => {
        const registro = solucionPorId(solucion.id)
        return { ...solucion, nombre: registro.nombre, to: registro.ruta, color: registro.color, cta: `Conocer ${registro.nombreCorto}` }
      }),
      /** Índice del panel abierto. Siempre hay uno: el primero al cargar. */
      activa: 0
    }
  },
  methods: {
    /**
     * alternar
     * Abre la solución elegida. Si ya estaba abierta, la cierra y abre la contigua
     * (la siguiente, o la anterior cuando es la última).
     */
    alternar(indice) {
      if (indice !== this.activa) {
        this.activa = indice
        return
      }
      const esUltima = indice === this.soluciones.length - 1
      this.activa = esUltima ? indice - 1 : indice + 1
    }
  }
}
</script>

<style scoped>
.solucion {
  border-color: #DEE2E6;
  transition:
    border-color var(--duration-gob) var(--ease-gob-out),
    box-shadow var(--duration-gob) var(--ease-gob-out);
}

.solucion-punto {
  background: var(--solucion-color);
}

.solucion-check {
  color: var(--solucion-color);
}

.acordeon-panel {
  display: grid;
  grid-template-rows: 0fr;
  transition: grid-template-rows var(--duration-gob) var(--ease-gob-out);
}

.acordeon-panel.is-abierto {
  grid-template-rows: 1fr;
}

.acordeon-chevron {
  transition:
    transform var(--duration-gob) var(--ease-gob-out),
    color var(--duration-gob) var(--ease-gob-out);
}

.solucion:hover .acordeon-chevron {
  color: var(--solucion-color);
}

.acordeon-boton[aria-expanded="true"] .acordeon-chevron {
  transform: rotate(180deg);
}
</style>
